import * as THREE from 'three';
import { CLASSICAL } from '../materials';
import { Figure, type ModelProps, Pole } from '../parts';

/** Classical-era units: each sits on y = 0, faces +z, at real proportions (UnitModel scales them). */

const { WOOD, DARK_WOOD, METAL, STRAW, HORSE } = CLASSICAL;

const G = {
  hat: new THREE.ConeGeometry(0.12, 0.06, 10),
  hood: new THREE.ConeGeometry(0.095, 0.17, 8),
  helmet: new THREE.SphereGeometry(0.085, 10, 6, 0, Math.PI * 2, 0, Math.PI / 2),
  axeHead: new THREE.BoxGeometry(0.08, 0.05, 0.02),
  spearTip: new THREE.ConeGeometry(0.025, 0.08, 5),
  shield: new THREE.CylinderGeometry(0.11, 0.11, 0.025, 12),
  shieldBoss: new THREE.SphereGeometry(0.03, 8, 6),
  wagonBed: new THREE.BoxGeometry(0.2, 0.08, 0.36),
  wagonCover: new THREE.CylinderGeometry(0.11, 0.11, 0.3, 10, 1, false, -Math.PI / 2, Math.PI),
  wheel: new THREE.CylinderGeometry(0.065, 0.065, 0.03, 10),
  // Archer
  bow: new THREE.TorusGeometry(0.16, 0.012, 5, 12, Math.PI),
  quiver: new THREE.CylinderGeometry(0.035, 0.03, 0.2, 6),
  // Cavalry
  horseBody: new THREE.BoxGeometry(0.13, 0.12, 0.3),
  horseNeck: new THREE.BoxGeometry(0.07, 0.16, 0.07),
  horseHead: new THREE.BoxGeometry(0.06, 0.06, 0.12),
  horseLeg: new THREE.CylinderGeometry(0.018, 0.015, 0.14, 5),
  saddle: new THREE.BoxGeometry(0.15, 0.03, 0.14),
  // Ram
  ramLog: new THREE.CylinderGeometry(0.05, 0.05, 0.5, 8).rotateX(Math.PI / 2),
  ramHead: new THREE.CylinderGeometry(0.065, 0.065, 0.06, 8).rotateX(Math.PI / 2),
  ramRoof: new THREE.CylinderGeometry(0.17, 0.17, 0.4, 3).rotateX(Math.PI / 2),
};

/** Four wheels at ±x, ±z (wagons and the ram). */
function Wheels({ x, z }: { x: number; z: number }) {
  return (
    <>
      {[-x, x].flatMap((wx) =>
        [-z, z].map((wz) => (
          <mesh
            key={`${wx}:${wz}`}
            geometry={G.wheel}
            material={DARK_WOOD}
            position={[wx, 0.065, wz]}
            rotation={[0, 0, Math.PI / 2]}
          />
        ))
      )}
    </>
  );
}

/** Straw hat and an axe over the shoulder. */
export function Worker({ accent }: ModelProps) {
  return (
    <>
      <Figure accent={accent} />
      <mesh geometry={G.hat} material={STRAW} position={[0, 0.44, 0]} />
      <Pole length={0.34} position={[0.13, 0.26, 0]} tilt={-0.35} />
      <mesh
        geometry={G.axeHead}
        material={METAL}
        position={[0.19, 0.41, 0]}
        rotation={[0, 0, -0.35]}
      />
    </>
  );
}

/** Pointed hood in team colour and a tall walking staff. */
export function Scout({ accent }: ModelProps) {
  return (
    <>
      <Figure accent={accent} />
      <mesh geometry={G.hood} material={accent} position={[0, 0.46, 0]} />
      <Pole length={0.55} position={[0.14, 0.27, 0.02]} />
    </>
  );
}

/** Helmet, round team-coloured shield, and a spear. */
export function Warrior({ accent }: ModelProps) {
  return (
    <>
      <Figure accent={accent} />
      <mesh geometry={G.helmet} material={METAL} position={[0, 0.38, 0]} />
      <mesh
        geometry={G.shield}
        material={accent}
        position={[-0.1, 0.2, 0.1]}
        rotation={[Math.PI / 2, 0, 0]}
      />
      <mesh geometry={G.shieldBoss} material={METAL} position={[-0.105, 0.2, 0.115]} />
      <Pole length={0.62} position={[0.14, 0.3, 0]} />
      <mesh geometry={G.spearTip} material={METAL} position={[0.14, 0.65, 0]} />
    </>
  );
}

/** Team-coloured quiver on the back and a bow held upright. */
export function Archer({ accent }: ModelProps) {
  return (
    <>
      <Figure accent={accent} />
      <mesh geometry={G.hood} material={WOOD} position={[0, 0.46, 0]} />
      <mesh
        geometry={G.quiver}
        material={accent}
        position={[-0.06, 0.26, -0.1]}
        rotation={[0.3, 0, 0.2]}
      />
      <mesh
        geometry={G.bow}
        material={DARK_WOOD}
        position={[0.14, 0.26, 0.02]}
        rotation={[0, Math.PI / 2, -Math.PI / 2]}
      />
      <Pole length={0.32} position={[0.14, 0.26, 0.02]} material={STRAW} />
    </>
  );
}

/** A horse with a team-coloured saddle cloth, and a rider with a lance. */
export function Cavalry({ accent }: ModelProps) {
  return (
    <>
      <mesh geometry={G.horseBody} material={HORSE} position={[0, 0.2, 0]} />
      <mesh
        geometry={G.horseNeck}
        material={HORSE}
        position={[0, 0.3, 0.14]}
        rotation={[0.5, 0, 0]}
      />
      <mesh geometry={G.horseHead} material={HORSE} position={[0, 0.37, 0.2]} />
      {[-0.045, 0.045].flatMap((x) =>
        [-0.1, 0.1].map((z) => (
          <mesh key={`${x}:${z}`} geometry={G.horseLeg} material={HORSE} position={[x, 0.07, z]} />
        ))
      )}
      <mesh geometry={G.saddle} material={accent} position={[0, 0.27, -0.02]} />
      <group position={[0, 0.21, -0.02]} scale={0.8}>
        <Figure accent={accent} />
        <mesh geometry={G.helmet} material={METAL} position={[0, 0.38, 0]} />
      </group>
      <Pole length={0.5} position={[0.1, 0.42, 0.08]} tilt={0} />
      <mesh geometry={G.spearTip} material={METAL} position={[0.1, 0.7, 0.08]} />
    </>
  );
}

/** A wheeled frame under a team-coloured roof, swinging an iron-capped log. */
export function Ram({ accent }: ModelProps) {
  return (
    <>
      <Wheels x={0.12} z={0.14} />
      <mesh geometry={G.wagonBed} material={WOOD} position={[0, 0.12, 0]} />
      <mesh geometry={G.ramLog} material={DARK_WOOD} position={[0, 0.2, 0.06]} />
      <mesh geometry={G.ramHead} material={METAL} position={[0, 0.2, 0.33]} />
      <mesh geometry={G.ramRoof} material={accent} position={[0, 0.34, 0]} />
    </>
  );
}

/** Covered wagon with a team-coloured canopy. */
export function Settler({ accent }: ModelProps) {
  return (
    <>
      <Wheels x={0.12} z={0.11} />
      <mesh geometry={G.wagonBed} material={WOOD} position={[0, 0.13, 0]} />
      <mesh
        geometry={G.wagonCover}
        material={accent}
        position={[0, 0.17, 0]}
        rotation={[-Math.PI / 2, 0, 0]}
      />
    </>
  );
}
